/**
 * LeadsPanel — unified admin view aggregating every lead source in one
 * table. Backed by `GET /api/admin/leads`.
 *
 * Sources surfaced:
 *  - Contact form, Brochure request, Resource download, Assessment lead,
 *    Portfolio lead, Trial request, ERIO (and other sister apps via
 *    external_leads).
 *
 * Per-row drill-down opens the source-specific admin tab (we don't
 * duplicate the detail UI — each source already has its own panel).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  RefreshCw, Search, Inbox, FileText, Download, Target,
  Briefcase, Rocket, Satellite, Mail, Building2,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const SOURCE_META = {
  contact:    { label: "Contact",    Icon: Mail,        pill: "ek-surface-2 ek-text-soft ek-line" },
  brochure:   { label: "Brochure",   Icon: FileText,    pill: "bg-indigo-50 text-indigo-700 border-indigo-300" },
  resource:   { label: "Resource",   Icon: Download,    pill: "bg-amber-50 text-amber-800 border-amber-300" },
  assessment: { label: "Assessment", Icon: Target,      pill: "bg-emerald-50 text-emerald-800 border-emerald-300" },
  portfolio:  { label: "Portfolio",  Icon: Briefcase,   pill: "bg-sky-50 text-sky-800 border-sky-300" },
  trial:      { label: "Trial",      Icon: Rocket,      pill: "bg-[#F4ECFD] text-[#7737F6] border-[#D6BFFB]" },
  erio:       { label: "ERIO",       Icon: Satellite,   pill: "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-300" },
};

const SOURCE_TO_TAB = {
  contact:    "Contacts",
  brochure:   "Brochure requests",
  resource:   "Resource leads",
  assessment: "Assessment leads",
  portfolio:  "Portfolio leads",
  trial:      "Trials",
  erio:       null, // no dedicated drill-down yet — show inline modal
};

const FILTERS = [
  { id: "all", label: "All sources" },
  { id: "contact", label: "Contact" },
  { id: "brochure", label: "Brochure" },
  { id: "resource", label: "Resource" },
  { id: "assessment", label: "Assessment" },
  { id: "portfolio", label: "Portfolio" },
  { id: "trial", label: "Trial" },
  { id: "erio", label: "ERIO" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

function SourcePill({ source }) {
  const m = SOURCE_META[source] || { label: source, Icon: Inbox, pill: "ek-surface-2 ek-text-soft ek-line" };
  const { Icon } = m;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10.5px] tracking-[0.12em] uppercase border ${m.pill}`}
      data-testid={`leads-source-pill-${source}`}
    >
      <Icon size={11} /> {m.label}
    </span>
  );
}

function FilterChip({ id, label, count, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase border transition-colors ${
        active
          ? "bg-black text-white border-black"
          : "ek-surface ek-text-soft ek-line hover:border-black"
      }`}
      data-testid={`leads-filter-${id}`}
    >
      {label}{typeof count === "number" ? ` (${count})` : ""}
    </button>
  );
}

function LeadRow({ row, onDrillDown }) {
  return (
    <tr className="border-t ek-line align-top" data-testid={`leads-row-${row.id}`}>
      <td className="px-3 py-3"><SourcePill source={row.source} /></td>
      <td className="px-3 py-3 text-[12.5px]">
        <div className="font-medium">{row.full_name || <span className="ek-text-muted">—</span>}</div>
        <div className="ek-text-muted inline-flex items-center gap-1">
          <Mail size={11} /> {row.email || "—"}
        </div>
      </td>
      <td className="px-3 py-3 text-[12.5px]">
        <div className="font-medium inline-flex items-center gap-1">
          <Building2 size={12} className="ek-text-muted" /> {row.organisation || <span className="ek-text-muted">—</span>}
        </div>
        <div className="ek-text-muted text-[11.5px]">{row.role || "—"}</div>
      </td>
      <td className="px-3 py-3 text-[12.5px] ek-text-soft">{row.interest || <span className="ek-text-muted">—</span>}</td>
      <td className="px-3 py-3 text-[11.5px] ek-text-muted">{fmtDate(row.created_at)}</td>
      <td className="px-3 py-3 text-right">
        <button
          type="button"
          onClick={() => onDrillDown(row)}
          className="text-[11px] tracking-[0.12em] uppercase text-[#7737F6] hover:underline"
          data-testid={`leads-drilldown-${row.id}`}
        >
          Open ↗
        </button>
      </td>
    </tr>
  );
}

export default function LeadsPanel({ onTabSwitch }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [drilldown, setDrilldown] = useState(null); // row obj for inline modal (e.g. ERIO)

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter && filter !== "all") params.set("source", filter);
      if (query.trim()) params.set("q", query.trim());
      const r = await api.get(`/admin/leads?${params.toString()}`);
      setData(r.data);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 403) toast.error("You don't have access to the unified leads view.");
      else toast.error("Couldn't load leads.");
    } finally {
      setLoading(false);
    }
  }, [filter, query]);

  useEffect(() => {
    const id = setTimeout(load, query ? 350 : 0); // debounce search
    return () => clearTimeout(id);
  }, [load, query]);

  const counts = useMemo(() => data?.counts_by_source || {}, [data]);

  const grandTotal = useMemo(() => Object.values(counts).reduce((s, n) => s + (n || 0), 0), [counts]);

  const onDrillDown = (row) => {
    const tab = SOURCE_TO_TAB[row.source];
    if (tab && typeof onTabSwitch === "function") {
      onTabSwitch(tab);
      toast.message(`Opened ${tab} — find this lead by email ${row.email}`);
    } else {
      // Inline modal for ERIO / external sources without a dedicated tab
      setDrilldown(row);
    }
  };

  return (
    <div data-testid="leads-panel">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="ek-heading text-xl">Leads — unified view</h2>
          <p className="text-[13px] ek-text-muted mt-1 max-w-2xl">
            Every lead from every Ekselens surface plus inbound from sister apps
            (ERIO et al). Filter by source, search by name/email/company, then drill
            into the source-specific tab for full detail.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {grandTotal > 0 && (
            <div className="text-[11px] tracking-[0.14em] uppercase ek-text-muted" data-testid="leads-grand-total">
              {grandTotal} lead{grandTotal === 1 ? "" : "s"}
            </div>
          )}
          <button
            type="button"
            onClick={load}
            className="ek-btn ek-btn-outline text-[12px] inline-flex items-center gap-2"
            data-testid="leads-refresh"
          >
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => (
          <FilterChip
            key={f.id}
            id={f.id}
            label={f.label}
            count={f.id === "all" ? grandTotal : counts[f.id]}
            active={filter === f.id}
            onClick={() => setFilter(f.id)}
          />
        ))}
      </div>

      <div className="relative mb-4 max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 ek-text-muted" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name, email, company, interest…"
          className="w-full border ek-line pl-9 pr-3 py-2 text-[13px] focus:outline-none focus:border-[#7737F6]"
          data-testid="leads-search-input"
        />
      </div>

      <div className="overflow-x-auto border ek-line">
        <table className="min-w-full text-[13px]">
          <thead className="ek-surface-2 ek-text-muted text-left text-[11px] tracking-[0.12em] uppercase">
            <tr>
              <th className="px-3 py-2">Source</th>
              <th className="px-3 py-2">Person</th>
              <th className="px-3 py-2">Organisation</th>
              <th className="px-3 py-2">Interest</th>
              <th className="px-3 py-2">When</th>
              <th className="px-3 py-2 text-right" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center ek-text-muted" data-testid="leads-loading">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && (data?.items || []).length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-12 text-center" data-testid="leads-empty">
                  <Inbox size={28} className="mx-auto ek-text-muted" />
                  <div className="ek-heading text-base mt-3">No leads match.</div>
                  <div className="text-[12.5px] ek-text-muted mt-1">
                    {filter !== "all" || query ? "Try widening your filter or search." : "Once leads come in, they'll appear here."}
                  </div>
                </td>
              </tr>
            )}
            {!loading && (data?.items || []).map((r) => (
              <LeadRow key={`${r.source}-${r.id}`} row={r} onDrillDown={onDrillDown} />
            ))}
          </tbody>
        </table>
      </div>

      {drilldown && (
        <div
          className="fixed inset-0 bg-black/60 z-40 flex items-center justify-center p-4"
          data-testid="leads-drilldown-modal"
        >
          <div className="ek-surface max-w-xl w-full">
            <div className="flex items-center justify-between px-6 py-4 border-b ek-line">
              <h3 className="ek-heading text-lg">Lead detail</h3>
              <button
                type="button"
                onClick={() => setDrilldown(null)}
                className="ek-text-muted hover:text-black"
                data-testid="leads-drilldown-close"
              >
                Close
              </button>
            </div>
            <div className="p-6 text-[13px] space-y-3">
              <div><SourcePill source={drilldown.source} /></div>
              <div><strong>{drilldown.full_name}</strong> &lt;{drilldown.email}&gt;</div>
              <div className="ek-text-soft">{drilldown.organisation} · {drilldown.role}</div>
              <div className="ek-text-soft">{drilldown.interest}</div>
              <div className="ek-text-muted text-[11.5px]">Captured {fmtDate(drilldown.created_at)}</div>
              {drilldown.extra && Object.keys(drilldown.extra).length > 0 && (
                <pre className="ek-surface-2 p-3 text-[11.5px] font-mono whitespace-pre-wrap break-all border ek-line">
                  {JSON.stringify(drilldown.extra, null, 2)}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
